import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { OptionSelector } from "./OptionSelector";
import { useCart } from "../context/CartContext";
import { fmt } from "../lib/api";

export function QuickView({ product, open, onClose }) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [selection, setSelection] = useState({ options: {}, priceDelta: 0, display: [] });

  if (!product) return null;
  const unitPrice = product.price + (selection.priceDelta || 0);

  const add = () => {
    addItem({ ...product, price: unitPrice }, qty, selection.options, selection.display);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="quick-view-modal"
        className="max-w-lg overflow-hidden rounded-2xl border-brand-line bg-brand-cream p-0 max-sm:h-[92dvh] max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-3xl max-sm:overflow-y-auto"
      >
        <div className="grid sm:grid-cols-2">
          <div className="aspect-square">
            {product.image && (
              <img src={product.image} alt={product.name} className="img-blend h-full w-full object-cover" />
            )}
          </div>
          <div className="flex flex-col p-6">
            <h3 className="font-display text-xl font-black leading-tight text-brand-forest">{product.name}</h3>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-2xl font-black text-brand-pine">{fmt(unitPrice)}</span>
              {product.comparePrice && (
                <span className="text-sm text-brand-ink/45 line-through">{fmt(product.comparePrice)}</span>
              )}
            </div>
            <div className="mt-4 flex-1 overflow-y-auto">
              <OptionSelector
                product={product}
                onChange={(s) => setSelection((prev) => (JSON.stringify(prev) === JSON.stringify(s) ? prev : s))}
              />
            </div>
            <div className="mt-5 flex items-center gap-3">
              <div className="flex items-center rounded-full border border-brand-line bg-white">
                <button data-testid="quickview-qty-minus" aria-label="Decrease quantity" onClick={() => setQty(Math.max(1, qty - 1))} className="p-2 px-3">
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-6 text-center text-sm font-bold" data-testid="quickview-qty">{qty}</span>
                <button data-testid="quickview-qty-plus" aria-label="Increase quantity" onClick={() => setQty(qty + 1)} className="p-2 px-3">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                data-testid="quickview-add-to-cart"
                onClick={add}
                disabled={!product.inStock}
                className="flex-1 rounded-full bg-brand-pine py-3 text-sm font-bold text-brand-cream transition-transform hover:scale-[1.02] disabled:opacity-40"
              >
                Add to Cart
              </button>
            </div>
            <Link
              to={`/product/${product.slug}`}
              data-testid="quickview-full-details"
              onClick={onClose}
              className="mt-3 text-center text-xs font-medium text-brand-pine underline underline-offset-4"
            >
              View full details
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
